import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { History } from 'lucide-react';
import type { ActivityDto } from '@cw/shared';
import { Avatar } from '@/design-system/components/avatar';
import { Button } from '@/design-system/components/button';
import { Skeleton } from '@/design-system/components/skeleton';
import { EmptyState, ErrorState } from '@/design-system/components/states';
import { capitalizeFirst } from '@/lib/utils';
import { activityText } from './activity-text';

type Feed = UseInfiniteQueryResult<InfiniteData<{ items: ActivityDto[]; nextBefore: string | null }>, Error>;

const dayLabel = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return 'Hoje';
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem';
  return capitalizeFirst(
    d.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}),
    }),
  );
};

/** Linha do tempo agrupada por dia, do mais recente para o mais antigo (SPEC §24 e §43). */
export function ActivityFeed({ feed, emptyText }: { feed: Feed; emptyText: string }) {
  if (feed.isPending)
    return (
      <div className="flex max-w-2xl flex-col gap-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10" />
        ))}
      </div>
    );
  if (feed.isError) return <ErrorState error={feed.error} onRetry={() => feed.refetch()} />;
  const items = feed.data.pages.flatMap((p) => p.items);
  if (!items.length)
    return <EmptyState icon={<History />} title="Nada registrado ainda" description={emptyText} />;

  const groups: { key: string; items: ActivityDto[] }[] = [];
  for (const it of items) {
    const k = new Date(it.createdAt).toDateString();
    const last = groups.at(-1);
    if (last?.key === k) last.items.push(it);
    else groups.push({ key: k, items: [it] });
  }

  return (
    <div className="max-w-2xl">
      {groups.map((g) => (
        <section key={g.key} className="mb-8">
          <h3 className="mb-3 text-[13px] font-medium text-fg-muted">{dayLabel(g.items[0]!.createdAt)}</h3>
          <ol className="relative ml-3 border-l border-line">
            {g.items.map((a) => (
              <li key={a.id} className="relative flex gap-3 pb-5 pl-6 last:pb-0">
                <span className="absolute top-1.5 -left-[5px] size-[9px] rounded-full bg-fg-subtle ring-4 ring-stage" />
                <Avatar name={a.actorName} src={a.actorAvatarUrl} size="xs" />
                <p className="min-w-0 flex-1 text-[13.5px] text-fg-muted">
                  <span className="font-medium text-fg">{a.actorName}</span> {activityText(a)}
                </p>
                <time className="tabular shrink-0 text-[12px] text-fg-subtle" dateTime={a.createdAt}>
                  {new Date(a.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </time>
              </li>
            ))}
          </ol>
        </section>
      ))}
      {feed.hasNextPage && (
        <Button loading={feed.isFetchingNextPage} onClick={() => feed.fetchNextPage()}>
          Carregar mais antigos
        </Button>
      )}
    </div>
  );
}
